/**
 * Contrato do BFF owner (`/web/*`) em DUAS camadas explícitas — a raiz da
 * reconciliação I2 (DI2-2):
 *
 *  1. **wire** (`OwnerWeb*`, `*Dto`) — o que trafega de verdade: snake_case, os 7
 *     status reais de envelope, os 6 de participante, nulidade honesta. Vem
 *     importado de `@argink/shared/owner-web`; nenhum campo aqui é "assumido".
 *  2. **modelo de UI** (`Envelope*`, `Participant`, `VerificationReport`) —
 *     camelCase, consumido por páginas e componentes.
 *
 * A tradução entre as duas vive **só** em `./adapters.ts`, nos dois sentidos, com
 * round-trip testado. Nenhuma página/componente converte nome de campo.
 *
 * **Modalidade tem dois enums, de propósito** (não é duplicata):
 * `SignatureLevel` (3 valores, LEITURA) é o enum real do Prisma e inclui
 * `avancada` — um envelope criado por `/v1` com nível avançado chega na lista do
 * owner, e negar isso era o que produzia badge vazio. `SignatureModality`
 * (2 valores, CRIAÇÃO) é o que a UI pode oferecer: Avançada está fora do MVP e
 * não aparece como recurso disponível em lugar nenhum (mandato §1/§5).
 *
 * A camada 1 é **importada** de `@argink/shared/owner-web` (DI2-1) — subpath, nunca
 * o index: o index reexporta `webhook-hmac`, que puxa `node:crypto` para o
 * bundle do browser. O guard de build vive em `vite.config.ts` (GATE DI2-A) e o
 * guard estático em `test/unit/browser-safety.test.ts`.
 */

import type {
  OwnerWebDashboardAttentionKind,
  OwnerWebDeadlinePreset,
  OwnerWebEnvelopeStatus,
  OwnerWebEnvelopeView as SharedOwnerWebEnvelopeView,
  OwnerWebParticipantRole,
  OwnerWebParticipantStatus,
  OwnerWebParticipantView as SharedParticipantView,
  OwnerWebSignatureLevel,
  VisualPlacement,
} from '@argink/shared/owner-web';

// ===========================================================================
// Enums do domínio — fonte única, em array `as const` para dar exaustividade
// verificável em runtime (não só em compile time).
// ===========================================================================

/** Enum REAL de `apps/api/prisma/schema.prisma` (`EnvelopeStatus`). São 7 — o
 * valor `active` que o front assumia NUNCA é emitido pelo backend. */
export const ENVELOPE_STATUS_VALUES = [
  'draft',
  'ready',
  'in_progress',
  'completed',
  'declined',
  'expired',
  'cancelled',
] as const;
export type EnvelopeStatus = (typeof ENVELOPE_STATUS_VALUES)[number];

/** Enum REAL de `apps/api/prisma/schema.prisma` (`SignerStatus`). São 6 —
 * `notified` ("convite enviado") e `in_session` ("assinando agora") são
 * exatamente o que o tracking do mandato §21 precisa mostrar. */
export const PARTICIPANT_STATUS_VALUES = [
  'pending',
  'notified',
  'in_session',
  'signed',
  'declined',
  'expired',
] as const;
export type ParticipantStatus = (typeof PARTICIPANT_STATUS_VALUES)[number];

/**
 * **Leitura** — enum REAL de `apps/api/prisma/schema.prisma` (`SignatureLevel`).
 * São 3: `avancada` existe no motor e um envelope criado por `/v1` com nível
 * avançado **chega na lista do owner**. O contrato do owner web jurava que isso
 * era impossível; na prática renderizava badge vazio. Aqui `avancada` é um valor
 * possível na leitura, rotulado de forma honesta — nunca fingido de outra coisa.
 */
export const SIGNATURE_LEVEL_VALUES = ['simples', 'avancada', 'qualificada'] as const;
export type SignatureLevel = (typeof SIGNATURE_LEVEL_VALUES)[number];

/**
 * **Criação** — subconjunto oferecível na UI (mandato §1/§5: "Modalidades apenas
 * Simples e Qualificada. Avançada fora do MVP — não pode aparecer como recurso
 * disponível em lugar nenhum da UI"). Ler um valor legado ≠ oferecê-lo.
 */
export const SIGNATURE_MODALITY_VALUES = ['simples', 'qualificada'] as const;
export type SignatureModality = (typeof SIGNATURE_MODALITY_VALUES)[number];

export const PARTICIPANT_ROLE_VALUES = ['signer', 'observer'] as const;
export type ParticipantRole = (typeof PARTICIPANT_ROLE_VALUES)[number];

/**
 * Enum REAL de `apps/api/prisma/schema.prisma` (`CeremonyMode`) — os 4 modos de
 * cerimônia (R-UX02-P02/L3B-B). Substitui o antigo `workflow` (`any_order`/`staged`,
 * lossy): aquele campo derivava dois valores de UI a partir de quatro modos reais
 * do backend, perdendo a diferença entre `sequential`, `parallel_quorum` e
 * `batch_async`. O detalhe agora lê `ceremony_mode` direto — sem derivação no
 * cliente.
 */
export const CEREMONY_MODE_VALUES = [
  'unitary',
  'sequential',
  'parallel_quorum',
  'batch_async',
] as const;
export type CeremonyMode = (typeof CEREMONY_MODE_VALUES)[number];

/** Enum REAL de `apps/api/prisma/schema.prisma` (`DocumentFormat`). O motor hoje
 * só assina PAdES/CAdES (XAdES é P1) — mas a LEITURA aceita os 3, como já faz
 * `SignatureLevel` (não é o mesmo cliente que oferece na criação). */
export const DOCUMENT_FORMAT_VALUES = ['pades', 'cades', 'xades'] as const;
export type DocumentFormat = (typeof DOCUMENT_FORMAT_VALUES)[number];

export const DEADLINE_PRESET_VALUES = ['24h', '3d', '7d', '15d', '30d', 'custom'] as const;
export type DeadlinePreset = (typeof DEADLINE_PRESET_VALUES)[number];

export const VERIFICATION_VERDICT_VALUES = ['PASSED', 'FAILED', 'INDETERMINATE'] as const;
export type VerificationVerdict = (typeof VERIFICATION_VERDICT_VALUES)[number];

export const VERIFICATION_CHECK_STATUS_VALUES = [
  'passed',
  'failed',
  'indeterminate',
  'skipped',
] as const;
export type VerificationCheckStatus = (typeof VERIFICATION_CHECK_STATUS_VALUES)[number];

// ===========================================================================
// Camada 1 — wire (o que o backend realmente envia/recebe). snake_case.
// ===========================================================================

/**
 * As formas abaixo NÃO são redefinidas: vêm do contrato canônico. Se um campo
 * mudar lá, o compilador acusa aqui — que é o ponto de ter uma fonte só.
 */
export type {
  OwnerWebCreateEnvelopePayload,
  OwnerWebEnvelopeList,
  /** Item de `GET /web/envelopes` (R-UX02-P02/L3A-1, endurecido em L3A-2a) — estende
   * `OwnerWebEnvelopeSummary` com `document_id`/`last_activity_at`, os dois OBRIGATÓRIOS.
   * `OwnerWebEnvelopeSummary` sozinho continua sendo o shape do Dashboard — não mexer. */
  OwnerWebEnvelopeListItem,
  OwnerWebEnvelopeSummary,
  OwnerWebParticipantInput,
  OwnerWebResendRequest,
  OwnerWebStartEnvelopeRequest,
  /** Posicionamento do carimbo visível (D-027) — mesma forma da `/v1`, nunca redefinida aqui. */
  VisualPlacement,
  /** `GET /web/dashboard` (R-UX02-P01/D-100/L3B) — read model dedicado da Home. */
  OwnerWebDashboardResponse,
  OwnerWebDashboardMetrics,
  OwnerWebDashboardAttentionItem,
  OwnerWebDashboardRecentItem,
} from '@argink/shared/owner-web';

/**
 * Participante do detalhe — o TIPO CANÔNICO do shared, sem alargamento local.
 *
 * Até a Correção A da R-UX02-P02/L3B (S1), o front alargava aqui os níveis de
 * assinatura de 2 para 3 valores porque o shared só declarava os de CRIAÇÃO
 * (`simples|qualificada`) e o Prisma tem `avancada`. O shared agora distingue
 * LEITURA (`OwnerWebSignatureLevelRead`, 3 valores) de criação
 * (`OwnerWebSignatureLevel`, 2 valores — `OwnerWebParticipantInput` continua com
 * eles), então o alargamento sumiu: um único contrato, o compilador acusa
 * qualquer divergência.
 */
export type OwnerWebParticipantView = SharedParticipantView;

/**
 * Documento do detalhe (`GET /web/envelopes/:id`, R-UX02-P02/L3B-B) — DERIVADO
 * do DTO canônico (`SharedOwnerWebEnvelopeView['document']`,
 * `@argink/shared/owner-web`), nunca redefinido campo a campo aqui (B4/Correção
 * B): duas cópias manuais do mesmo shape são exatamente como um campo novo no
 * shared passa batido pelo web sem o compilador acusar. `NonNullable` porque o
 * `null` no DTO raiz significa "sem documento" — este tipo descreve só o CASO
 * em que o documento existe.
 */
export type OwnerWebEnvelopeDetailDocument = NonNullable<SharedOwnerWebEnvelopeView['document']>;

/**
 * DTO wire do detalhe — É o `OwnerWebEnvelopeView` do shared (`@argink/shared/owner-web`),
 * nunca redefinido manualmente (B4/Correção B: "o web não redefine o DTO raiz do detalhe").
 * `workflow` (`any_order`/`staged`, lossy) não existe mais em lugar nenhum: o backend expõe
 * `ceremony_mode` (os 4 valores reais) e `quorum` direto. `document` é `null | {...}` — três
 * casos distintos no título ("sem documento associado", "documento sem nome", nome real),
 * nunca colapsados. `participants` já vem com o nível de LEITURA de 3 valores (S1).
 */
export type OwnerWebEnvelopeView = SharedOwnerWebEnvelopeView;

/**
 * Travas de paridade em COMPILAÇÃO com o contrato canônico. Os arrays acima
 * existem porque exaustividade em runtime precisa de valores, não de tipos — e
 * um array escrito à mão poderia derivar do contrato sem ninguém notar. Aqui
 * não pode: se as uniões deixarem de coincidir, isto não compila.
 *
 * Terceiro elo da corrente: `test/unit/status.test.ts` compara os mesmos arrays
 * com o `schema.prisma`. Contrato ⇄ front ⇄ banco, os três amarrados.
 */
type MutuallyAssignable<A, B> = [A] extends [B] ? ([B] extends [A] ? true : false) : false;

export const CONTRACT_PARITY: {
  envelopeStatus: MutuallyAssignable<EnvelopeStatus, OwnerWebEnvelopeStatus>;
  participantStatus: MutuallyAssignable<ParticipantStatus, OwnerWebParticipantStatus>;
  participantRole: MutuallyAssignable<ParticipantRole, OwnerWebParticipantRole>;
  deadlinePreset: MutuallyAssignable<DeadlinePreset, OwnerWebDeadlinePreset>;
  /** CRIAÇÃO: os 2 valores oferecíveis. A leitura (3) é o alargamento acima. */
  signatureModality: MutuallyAssignable<SignatureModality, OwnerWebSignatureLevel>;
} = {
  envelopeStatus: true,
  participantStatus: true,
  participantRole: true,
  deadlinePreset: true,
  signatureModality: true,
};

export interface VerificationCheckDto {
  name: string;
  status: VerificationCheckStatus;
  reasons: string[];
  detail: Record<string, unknown>;
  notes: string[];
}

export interface VerificationSummaryDto {
  passed: number;
  failed: number;
  indeterminate: number;
  skipped: number;
  failedChecks: string[];
  indeterminateChecks: string[];
  reasons: string[];
}

/**
 * `packages/evidence-verifier/src/types.ts` → `VerificationReport`, devolvido
 * **puro** pela rota ("sem remodelar: notes, checks e limites honestos sao
 * contrato"). **Não existe `checkedAt`** — o relatório é remontado e reverificado
 * a cada chamada; quem carimba o instante é o front (`receivedAt` no modelo de UI).
 *
 * `name`/`reasons` são `string` de propósito: são uniões fechadas DO VERIFICADOR
 * e o front só as exibe. Um check novo no backend precisa aparecer na tela, não
 * quebrar a tela — o que exige exaustividade aqui é status (semântica), não
 * vocabulário técnico reproduzido.
 */
export interface VerificationReportDto {
  verdict: VerificationVerdict;
  verifierVersion: string;
  bundleFormatId: string;
  manifestHash: string | null;
  manifestSchemaVersion: number | null;
  sealKeyId: string | null;
  manifest: unknown;
  checks: VerificationCheckDto[];
  summary: VerificationSummaryDto;
  notes: string[];
}

// ===========================================================================
// Camada 2 — modelo de UI. camelCase. Sem perda de informação em relação ao
// wire (o round-trip DTO → UI → DTO é testado e precisa devolver o original).
// ===========================================================================

export interface SessionUser {
  id: string;
  name: string;
  email: string;
}

/** Capacidades que a sessão de owner declara para este ambiente (D-109/R-UX02-P02).
 * A UI só oferece o que a própria sessão confirma disponível — nunca infere
 * disponibilidade a partir de um 404 de rota desligada. */
export interface SessionCapabilities {
  /** `true` só quando `/web/envelopes/:id/evidence{,/manifest,/bundle}` responde
   * de verdade nesta instalação. `false` não é erro: é o ambiente dizendo que
   * a entrega do pacote de evidências não está habilitada aqui. */
  evidenceDelivery: boolean;
}

export interface SessionInfo {
  user: SessionUser;
  membership: { id: string; role: 'owner' };
  tenant: { id: string };
  expiresAt: string;
  /** Só `/web/session` declarando `environment: 'development'` libera o link
   * "Abrir caixa de teste" (AMVP-10). Ausente ⇒ link oculto (degradação segura). */
  environment?: 'development' | 'production';
  capabilities: SessionCapabilities;
}

export interface Participant {
  id: string;
  name: string;
  /** `null` quando a PII foi apagada (D-029). A UI mostra ausência, não `''`. */
  email: string | null;
  role: ParticipantRole;
  /** `null` para observer; inteiro >=1 contíguo para signer. */
  stage: number | null;
  /** `null` só para observer. Pode ser `'avancada'` em envelope legado criado
   * por `/v1` — valor de LEITURA, nunca oferecido na criação. */
  requiredModality: SignatureLevel | null;
  /** Nasce igual ao `requiredModality` quando o signer assina — nunca rebaixa
   * (IMVP-15). `null` até assinar. */
  actualModality: SignatureLevel | null;
  status: ParticipantStatus;
  createdAt: string;
  updatedAt: string;
  /** Instante em que o canal (e-mail) foi verificado — `null` enquanto não foi. */
  channelVerifiedAt: string | null;
  signedAt: string | null;
  /** Motivo declarado na recusa — `null` fora de `declined`. */
  declineReason: string | null;
  /**
   * Carimbo visível deste participante (D-027, opt-in do wizard) — `null` = sem carimbo, que é
   * o default. Guarda o posicionamento INTEIRO, não um booleano: o wire manda as frações, e um
   * modelo de UI que só soubesse "tem/não tem" perderia informação que o round-trip do adapter
   * precisa devolver intacta.
   */
  visualStamp: VisualPlacement | null;
}

export interface EnvelopeDetailDocument {
  id: string;
  /** `null` quando o backend não guardou/expôs o nome — a UI usa
   * `documentDisplayName()` (`lib/copy.ts`), nunca `''` nem `!`. */
  name: string | null;
  format: DocumentFormat;
  hashSha256: string;
  /** `null` quando a contagem de páginas não foi apurada — nunca `0` fingido. */
  pageCount: number | null;
  createdAt: string;
  signed: boolean;
}

export interface EnvelopeDetail {
  id: string;
  status: EnvelopeStatus;
  ceremonyMode: CeremonyMode;
  /** M de N para `parallel_quorum`; `null` nos demais modos — a tela só mostra
   * quando não nulo. */
  quorum: number | null;
  /** Etapa liberada agora — vem do backend (DI2-4), nunca derivada no cliente. */
  currentStage: number | null;
  createdAt: string;
  updatedAt: string;
  /** `expires_at` do backend. **Pode ser `null`** (envelope sem prazo). */
  deadlineAt: string | null;
  /** `null` = envio sem documento associado nenhum — caso distinto de "documento
   * sem nome" (que tem `document` não-nulo com `name: null`). Os dois nunca se
   * colapsam no mesmo texto. Quem precisa só do nome lê `document?.name`
   * diretamente (`lib/copy.ts::documentDisplayName`) — este tipo não guarda mais
   * um espelho `documentName` (B2/Correção B): a cópia convidava a esquecer a
   * distinção entre "sem documento" e "documento sem nome", os dois caindo no
   * mesmo `null`. */
  document: EnvelopeDetailDocument | null;
  participants: Participant[];
  /** Contagens DERIVADAS de `participants` (contagem, não regra de negócio). */
  participantsTotal: number;
  participantsSigned: number;
}

export interface EnvelopeSummary {
  id: string;
  status: EnvelopeStatus;
  documentName: string | null;
  createdAt: string;
  updatedAt: string;
  deadlineAt: string | null;
  signerCount: number;
  observerCount: number;
  signedCount: number;
  /**
   * @deprecated Espelho de `signerCount`, mantido só para as telas migrarem sem
   * big-bang. Observadores nunca entraram nesta conta — o nome mente sobre isso.
   */
  participantsTotal: number;
  /** @deprecated Espelho de `signedCount`. Preferir `signedCount`. */
  participantsSigned: number;
}

/**
 * Item de `GET /web/envelopes` (R-UX02-P02/L3A-2b) — modelo de UI da LISTA global.
 * Estende `EnvelopeSummary` (o mesmo shape do Dashboard) com os dois campos que só a
 * lista consome, espelhando `OwnerWebEnvelopeListItem` do wire:
 *
 *  - `documentId` — `null` quando o envio não tem documento associado nenhum
 *    ("Envio sem documento associado"). Presente + `documentName: null` é o caso
 *    COMUM ("Documento sem nome") — os dois casos colapsavam no mesmo `null` antes
 *    deste campo existir, e é exatamente essa ambiguidade que ele resolve. A
 *    combinação `documentId === null && documentName !== null` é IMPOSSÍVEL e o
 *    adapter recusa a resposta antes de este tipo existir com essa forma (DI2-3).
 *  - `lastActivityAt` — `max(Envelope.updatedAt, Signer.updatedAt)`, calculado no
 *    SERVIDOR. NUNCA é conclusão/assinatura/recusa/cancelamento/envio de convite —
 *    só "quando algo mexeu por último".
 */
export interface EnvelopeListItem extends EnvelopeSummary {
  documentId: string | null;
  lastActivityAt: string;
}

export interface EnvelopeListResponse {
  items: EnvelopeListItem[];
  nextCursor: string | null;
  /** Todos os envios visíveis ao tenant, ANTES de `status`/`q`. */
  total: number;
  /** Os que satisfazem `status`/`q`, ANTES do cursor. */
  totalFiltered: number;
  /** `items.length` desta página — nunca a autoridade de `total`/`totalFiltered`. */
  totalReturned: number;
}

// ===========================================================================
// Dashboard (R-UX02-P01/D-100/L3B) — `GET /web/dashboard`, uma chamada só.
// Modelo de UI camelCase; a tradução mora só em `adapters.ts::toDashboardSnapshot`.
// ===========================================================================

/**
 * Motivo de um envio aparecer em "Requer sua atenção". `dueSoon` é a única
 * renomeação em relação ao wire (`due_soon` → `dueSoon`) — os demais valores já
 * eram palavras simples, sem separador. Fechado (6 valores): o adapter recusa
 * qualquer `kind` que `zOwnerWebDashboardAttentionKind` não valide antes mesmo
 * de chegar aqui — não existe ramo "desconhecido" neste tipo de propósito.
 */
export const DASHBOARD_ATTENTION_KIND_VALUES = [
  'declined',
  'expired',
  'overdue',
  'dueSoon',
  'ready',
  'stalled',
] as const;
export type DashboardAttentionKind = (typeof DASHBOARD_ATTENTION_KIND_VALUES)[number];

/** O que o campo `at` representa — nunca inferido como evento de assinatura.
 * Mesma renomeação de sempre (`expires_at`/`updated_at` → camelCase). */
export type DashboardAtKind = 'expiresAt' | 'updatedAt';

/**
 * Trava de paridade em COMPILAÇÃO, mesmo padrão de `CONTRACT_PARITY` acima — cobre
 * os 5 valores que não mudam de escrita; `due_soon` ⇄ `dueSoon` é a única
 * renomeação, provada em runtime pelo `Record` total de `AT_KIND_TO_UI`/
 * `ATTENTION_KIND_TO_UI` (`adapters.ts`), que não compila se um valor novo do
 * enum real chegar sem tradução.
 */
export const DASHBOARD_CONTRACT_PARITY: {
  attentionKind: MutuallyAssignable<
    Exclude<DashboardAttentionKind, 'dueSoon'>,
    Exclude<OwnerWebDashboardAttentionKind, 'due_soon'>
  >;
} = {
  attentionKind: true,
};

export interface DashboardMetrics {
  inProgress: number;
  dueWithin48h: number;
  completed: number;
  attention: number;
}

export interface DashboardAttentionItem {
  envelope: EnvelopeSummary;
  kind: DashboardAttentionKind;
  at: string;
  atKind: DashboardAtKind;
}

export interface DashboardRecentItem {
  envelope: EnvelopeSummary;
}

/** O snapshot inteiro que `GET /web/dashboard` devolve, já traduzido. */
export interface DashboardSnapshot {
  generatedAt: string;
  metrics: DashboardMetrics;
  attentionItems: DashboardAttentionItem[];
  recentItems: DashboardRecentItem[];
}

export interface VerificationCheck {
  name: string;
  status: VerificationCheckStatus;
  reasons: string[];
  detail: Record<string, unknown>;
  notes: string[];
}

export interface VerificationSummary {
  passed: number;
  failed: number;
  indeterminate: number;
  skipped: number;
  failedChecks: string[];
  indeterminateChecks: string[];
  reasons: string[];
}

export interface VerificationReport {
  verdict: VerificationVerdict;
  verifierVersion: string;
  bundleFormatId: string;
  manifestHash: string | null;
  manifestSchemaVersion: number | null;
  sealKeyId: string | null;
  manifest: unknown;
  /** `checks`/`summary` são o "detalhe técnico em expansão" do mandato §10 — o
   * front os EXIBE; jogá-los fora era perda de contrato. */
  checks: VerificationCheck[];
  summary: VerificationSummary;
  /** Notas literais do verificador — a UI reproduz, nunca omite (IMVP-27). */
  notes: string[];
  /**
   * Instante em que ESTE cliente recebeu a resposta. O backend **não** manda
   * `checkedAt`: o relatório é remontado e reverificado a cada chamada, então
   * carimbar aqui é a única afirmação verdadeira que se pode fazer sobre "quando".
   */
  receivedAt: string;
}

// ===========================================================================
// Erros
// ===========================================================================

export interface ApiErrorBody {
  error: string;
  message: string;
  /** `issues` do Zod quando o backend responde 400/422 de validação. */
  issues?: unknown;
}

export class ApiError extends Error {
  readonly status: number;
  readonly body: ApiErrorBody | null;
  /**
   * Segundos do header `retry-after`, quando o backend o envia — hoje só nos
   * `429` de autenticação (`OwnerAuthRateLimitedError` carrega `retryAfterSeconds`).
   *
   * Existe para a UI poder contar o tempo REAL do servidor. O contrato da rodada
   * proíbe countdown inventado no cliente (§21): sem este campo, a única
   * alternativa honesta seria não mostrar contagem nenhuma. `null` quando o
   * header não veio ou não é um inteiro de segundos — nunca um palpite.
   */
  readonly retryAfterSeconds: number | null;

  constructor(status: number, body: ApiErrorBody | null, retryAfterSeconds: number | null = null) {
    super(body?.message ?? `requisição falhou (HTTP ${status})`);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
    this.retryAfterSeconds = retryAfterSeconds;
  }

  /** Código estável emitido pelo backend (`{ error, message }`), quando houver. */
  get code(): string | null {
    return this.body?.error ?? null;
  }
}

/**
 * A resposta chegou, mas **não é** o contrato acordado — campo faltando, tipo
 * errado ou valor de enum que este cliente não conhece (status novo no backend,
 * por exemplo).
 *
 * Falha **fechada e ruidosa** de propósito (invariante "fail-closed por default"
 * do ARGINK): é preferível a tela dizer que não conseguiu interpretar o dado a
 * rotular um envelope desconhecido como "Em andamento". Nenhum fallback
 * silencioso — mandato §9 / DI2-3.
 */
export class ContractViolationError extends Error {
  readonly path: string;
  readonly received: unknown;

  constructor(path: string, received: unknown, detail?: string) {
    super(
      `resposta fora do contrato em "${path}"${detail ? `: ${detail}` : ''} (recebido: ${JSON.stringify(received)})`,
    );
    this.name = 'ContractViolationError';
    this.path = path;
    this.received = received;
  }
}

/**
 * Resultado da ação "Validar documento assinado" (D-063/#100) — tri-state HONESTO
 * do motor de validação, via BFF (POST /web/envelopes/:id/validate). `indeterminate`
 * significa "não foi possível confirmar toda a cadeia" — NUNCA é apresentado como
 * inválido, e nenhum campo promete aprovação de autoridade (D-051).
 */
export interface SignedDocumentValidation {
  result: 'valid' | 'invalid' | 'indeterminate';
  indication: string;
  subIndication: string | null;
  signatureCount: number;
  checkedAt: string;
}
